/**
 * MAHI — the first-run "what do you actually want to see" picker.
 *
 * Asked here, on the first landing at /discover, rather than as a third
 * onboarding step. Onboarding deliberately went from three screens to two —
 * every screen between signup and a product is a chance to lose someone — and
 * this question is different from the two that remain: it is answerable in one
 * tap ("Skip"), and its payoff is visible in the very next thing on screen.
 * Asking it in front of the feed, where the answer immediately changes what
 * you are looking at, is both cheaper and more legible than asking it in a
 * flow where nothing has been shown yet.
 *
 * Three ways out, all equal citizens:
 *   - Pick some categories and a ceiling.
 *   - "Show me everything" — every group, no cap.
 *   - "Skip for now" — same effect as everything, one tap.
 * All three record that the question has been answered, which is what stops it
 * ever appearing again. Skipping is an answer, not a deferral: re-asking
 * someone who declined is how a helpful question becomes nagging.
 *
 * Nothing here is a dead end either. The picks are editable forever from
 * Profile → What you see (pages/FeedPreferences), which shares this screen's
 * controls via PreferencePicker, and the feed itself widens (and says so, in
 * MAHI's voice) rather than running out — see
 * services/mahi/discovery_prefs.py.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import mahi, { errMsg, trackEvent } from '../../api';
import PreferencePicker from '../preferences/PreferencePicker';
import useFocusTrap from '../../hooks/useFocusTrap';

const springTransition = { type: 'spring', stiffness: 300, damping: 28 };

export default function PreferenceSetup({ onDone }) {
  const [options, setOptions] = useState(null);   // { categories, price_bands }
  const [picked, setPicked] = useState([]);       // group keys
  const [band, setBand] = useState('any');
  const [saving, setSaving] = useState(false);
  const panelRef = useRef(null);

  // No `onEscape`. Escape would have to mean either "skip" or "cancel", and
  // both are wrong: this is the only time we ask, so a keystroke that silently
  // answers it — or that dismisses it without answering, leaving it to reappear
  // on the next load — is worse than having no shortcut. The Skip button says
  // what it does.
  useFocusTrap(panelRef, { active: true });

  useEffect(() => {
    // The picker's own contents come from the server so the shopper-facing
    // groups and the taxonomy values they resolve to can't drift apart across
    // a deploy — see routes/mira/profile.py::get_discovery_preferences.
    mahi.get('/profile/discovery-preferences')
      .then(({ data }) => {
        setOptions(data.options);
        setPicked(data.categories || []);
        setBand(data.price_band || 'any');
      })
      // A picker we can't populate must not become a wall between someone and
      // the feed. Getting out of the way un-answered means they'll be asked
      // again next time, which is the right failure: the alternative is
      // recording "no preference" for a question they were never actually shown.
      .catch(() => onDone({ answered: false }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const allKeys = useMemo(() => (options?.categories || []).map((c) => c.key), [options]);
  const allPicked = allKeys.length > 0 && picked.length === allKeys.length;

  const toggle = (key) => setPicked((current) => (
    current.includes(key) ? current.filter((k) => k !== key) : [...current, key]
  ));

  const save = async (categories, priceBand, how) => {
    if (saving) return;
    setSaving(true);
    try {
      const { data } = await mahi.put('/profile/discovery-preferences', {
        categories, price_band: priceBand,
      });
      trackEvent('discovery_preferences_set', {
        how, category_count: categories.length, price_band: priceBand,
      });
      onDone({ answered: true, prefs: data });
    } catch (e) {
      // Deliberately NOT dismissed on failure, unlike the load path above.
      // There the user has seen nothing and loses nothing; here they have made
      // real choices, and closing the modal would throw those away while
      // leaving them believing their feed had been narrowed.
      toast.error(errMsg(e, 'Could not save that - try again?'));
      setSaving(false);
    }
  };

  return (
    <motion.div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      data-testid="preference-setup"
    >
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]" />

      <motion.div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="What would you like to see?"
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 40, opacity: 0 }}
        transition={springTransition}
        className="relative w-full max-w-md bg-white rounded-t-3xl sm:rounded-3xl px-5 pt-6 pb-6 max-h-[88vh] flex flex-col"
      >
        <div className="w-9 h-1 rounded-full bg-slate-200 mx-auto mb-4 sm:hidden" />

        {/* MAHI's own voice — mahiteal, serif, italic. Same treatment
            onboarding gives her lines, so this reads as her asking rather
            than as a settings form that happens to have appeared. */}
        <p className="text-sm text-mahiteal-600 font-mahi-serif italic text-center mb-4 px-2">
          &ldquo;Tell me roughly what you&apos;re after and I&apos;ll skip everything else.&rdquo;
        </p>
        <h2 className="font-mahi-serif text-2xl text-slate-900 text-center">
          What should I show you?
        </h2>
        <p className="text-[13px] text-slate-500 font-mahi-sans text-center mt-1 mb-5">
          Pick as many as you like - you can change this any time.
        </p>

        {!options ? (
          <div className="flex-1 min-h-[180px] flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto -mx-1 px-1">
            <PreferencePicker
              options={options}
              picked={picked}
              band={band}
              onToggleCategory={toggle}
              onSelectBand={setBand}
              disabled={saving}
            />
          </div>
        )}

        {/* Actions. "Show me everything" sits next to Skip rather than under
            the chips because they are the same decision reached two ways, and
            someone who wants no filter should not have to tap ten chips to
            express it. */}
        <div className="shrink-0 pt-5 flex flex-col gap-2">
          <button
            type="button"
            disabled={!options || saving}
            onClick={() => save(picked, band, allPicked ? 'everything' : 'picked')}
            data-testid="pref-save"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-raspberry-500 text-white px-6 py-3.5 font-medium font-mahi-sans hover:bg-raspberry-600 transition disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" />
              : picked.length ? `Show me these (${picked.length})` : 'Show me everything'}
          </button>
          <div className="flex items-center justify-center gap-5">
            <button
              type="button"
              disabled={!options || saving}
              onClick={() => { setPicked(allKeys); setBand('any'); }}
              data-testid="pref-select-all"
              className="text-[12px] text-slate-500 hover:text-slate-700 font-mahi-sans py-1.5 disabled:opacity-50"
            >
              Select all
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={() => save([], 'any', 'skipped')}
              data-testid="pref-skip"
              className="text-[12px] text-slate-500 hover:text-slate-700 font-mahi-sans py-1.5 disabled:opacity-50"
            >
              Skip for now
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
